import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useCallkitDataKeys } from '../model';
import { clearCall } from '@/app/store/commonSlice';
import { makeOneToOneAudioCall, makeOneToOneVideoCall, makeGroupCall, inviteUsers, endCall } from '../services/callService';
import { CALL_TYPE } from '@/shared/constants/callConstants';

/**
 * ViewModel for CallScreenRouter.
 * Owns: pendingCall listener (reads commonSlice, routes to call service), call screen visibility.
 * CallScreenRouter.jsx renders only — no SDK imports, no dispatch, no useEffect.
 */
export const useCallScreenRouter = () => {
   const dispatch = useDispatch();
   const { callScreenStatus, callUIState } = useCallkitDataKeys(['callScreenStatus', 'callUIState']);
   const pendingCall = useSelector(state => state.common?.pendingCall);

   const isCallActive = ['incoming', 'outgoing', 'connected'].includes(callScreenStatus);
   const isMaximized = callUIState === 'maximized';
   const showOverlay = isCallActive && isMaximized;

   // Listen to cross-feature call requests dispatched via commonSlice
   useEffect(() => {
      if (!pendingCall) return;

      const { userId, userIds, groupId, callType, isGroup, isInvite } = pendingCall;

      if (isInvite && userIds?.length > 0) {
         inviteUsers(userIds);
      } else if (isGroup && userIds?.length > 0) {
         const isVideoCall = callType === CALL_TYPE.VIDEO;
         makeGroupCall(userIds, groupId, isVideoCall);
      } else if (userId) {
         if (callType === CALL_TYPE.VIDEO) {
            makeOneToOneVideoCall(userId);
         } else {
            makeOneToOneAudioCall(userId);
         }
      }

      dispatch(clearCall());
   }, [pendingCall, dispatch]);

   useEffect(() => {
      const handleUnload = () => {
         if (isCallActive) {
            endCall();
         }
      };

      window.addEventListener('beforeunload', handleUnload);
      return () => window.removeEventListener('beforeunload', handleUnload);
   }, [isCallActive]);

   return {
      callScreenStatus,
      isCallActive,
      isMaximized,
      showOverlay,
   };
};
